import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getOneInvoice } from 'src/api/server/invoice';
import { getTransactions } from 'src/api/server/cashier';
import  { generateID, generateNumber } from 'src/utils/random';

// ----------------------------------------------------------------------

// REMOVED: fetchInvoicesData - Now using fetchTransactions from MongoDB only
// All dashboard data comes from MongoDB transactions, not NetSuite invoices

export const fetchTransactions = createAsyncThunk(
  'invoices/fetchTransactions',
  async ({ idUser, config, page, includeCancelled = false }) => {
    const { data } = (await getTransactions(idUser, config, page, includeCancelled)) || {}
    console.info('fetchTransactions data', data)
    return {
      results: data?.transactions || [],
      nextPage: data?.pagination?.nextPage,
      page,
      includeCancelled
    }
  }
);

export const fetchOneInvoice = createAsyncThunk(
  'invoices/fetchOneInvoice',
  async ({ idUser, config, id }) => {
    const invoice = await getOneInvoice(idUser, config, id);
    console.info('fetchOneInvoice invoice', invoice);
    return invoice;
  }
);

const initialState = { allData: [], drafts: [], syncs: [], isLoading: false, error: undefined, nextPage: 1 }

const slice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    resetInvoices: (state) => {
      // Remote invoice refreshes must never destroy local drafts or pending syncs.
      // Those are the only copy of a sale while an HTTP request is uncertain/offline.
      state.allData = [];
      state.isLoading = false;
      state.error = undefined;
      state.nextPage = 1;
    },
    updateInvoices: (state, action) => {
      const { data } = action.payload;
      const now = new Date().toISOString();

      data.forEach((invoice) => {
        // Remove drafts by idempotencyKey first (most reliable), then fallback to id and transactionNumber
        if (invoice.idempotencyKey) {
          state.drafts = state.drafts.filter(draft => draft.idempotencyKey !== invoice.idempotencyKey);
        }
        state.drafts = state.drafts.filter(draft => draft.id !== invoice.id);
        state.drafts = state.drafts.filter(draft => draft.transactionNumber !== invoice.transactionNumber);

        // Find existing to preserve config if not in new data
        const existing = state.allData.find((i) => i.id === invoice.id ||
            i.transactionNumber === invoice.transactionNumber ||
            (invoice.idempotencyKey && i.idempotencyKey === invoice.idempotencyKey));

        // Ensure config is present - use new data, or existing, or extract from metadata
        let configId = invoice.config;
        if (!configId && existing?.config) {
          configId = existing.config;
        }
        if (!configId && invoice.metadata?.config) {
          configId = invoice.metadata.config;
        }

        const normalizedInvoice = {
          ...invoice,
          config: configId,
          configId: configId,
          createdDate: (() => {
            if (!invoice.createdDate) return now;
            const date = new Date(invoice.createdDate);
            return !Number.isNaN(date.getTime()) ? date.toISOString() : now;
          })(),
          dueDate: (() => {
            if (!invoice.dueDate) return null;
            const date = new Date(invoice.dueDate);
            return !Number.isNaN(date.getTime()) ? date.toISOString() : null;
          })(),
          lastUpdatedDate: now,
          items:
            invoice.items?.map((item) => ({
              ...item,
              // Normaliser d'autres dates d'éléments si nécessaire
            })) || [],
        };

        if (existing) Object.assign(existing, normalizedInvoice);
        else state.allData.push(normalizedInvoice);
      });
    },
    removeInvoice: (state, action) => {
      state.allData = state.allData.filter((data) => ![data.id, data.transactionNumber].includes(action.payload));
    },
    addDraft: (state, action) => {
      const { payload } = action;
      const draftVerified = state.drafts.find((x) => x?.id === payload?.id || x?.transactionNumber === payload?.transactionNumber || (payload?.idempotencyKey && x?.idempotencyKey === payload?.idempotencyKey));
      if (!draftVerified){
        if (!payload.id) payload.id = `draft_${generateID(18)}`

        const now = new Date().toISOString();
        // Generate idempotencyKey only if not provided (for idempotency)
        if (!payload.idempotencyKey) {
          payload.idempotencyKey = `IDEM-${Date.now()}-${generateNumber(6)}`;
        }
        payload.transactionNumber = payload.transactionNumber || `TXN-${Date.now()}`;
        payload.invoiceNumber = `Invoice #DRAFT${generateNumber(6)}`;
        payload.status = { value: 'draft', label: 'draft' };
        payload.createdDate = now;
        payload.dueDate = now;
        payload.lastUpdatedDate = now;

        state.drafts.push(payload);
      }
    },
    removeDraft: (state, action) => {
      state.drafts = state.drafts.filter((data) => ![data.id, data.transactionNumber, data.idempotencyKey].includes(action.payload));
    },
    updateDraft: (state, action) => {
      const { payload } = action;
      const draftFound = state.drafts.find((x) => x?.id === payload.id || x?.transactionNumber === payload.transactionNumber);
      if (draftFound) {
        Object.entries(payload).forEach(([key, value]) => {
          if (!['createdDate', 'dueDate', 'lastUpdatedDate'].includes(key)) draftFound[key] = value;
        });
      }
    },
    addSync: (state, action) => {
      const { payload } = action;
      payload.invoiceNumber = `Invoice #SYNCHRONISING${payload.id}`;
      payload.status = { value: 'synchronising', label: 'synchronising_with_erp' };
      payload.createdDate = new Date().toString();
      payload.dueDate = new Date().toString();
      console.log('addSync payload', payload);
      state.syncs.push(payload);
    },
    removeSync: (state, action) => {
      state.syncs = state.syncs.filter(
        (data) => ![data.id, data.transactionNumber, data.idempotencyKey].includes(action.payload)
      );
    },
    updateSync: (state, action) => {
      const { payload } = action;
      const syncFound = state.syncs.find((x) =>
        x?.id === payload.id ||
        x?.transactionNumber === payload.transactionNumber ||
        (payload.idempotencyKey && x?.idempotencyKey === payload.idempotencyKey)
      );
      if (syncFound) {
        Object.entries(payload).forEach(([key, value]) => {
          syncFound[key] = value;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // REMOVED: fetchInvoicesData reducers - Now using fetchTransactions from MongoDB only
      .addCase(fetchTransactions.pending, (state) => {
        if (!state.allData[0]) {
          state.isLoading = true;
          state.error = undefined;
        }
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        const { results, nextPage, page } = action.payload;
        state.isLoading = false;
        state.error = undefined;
        state.nextPage = nextPage;

        // S'assurer que toutes les dates sont des chaînes et que config est présent
        const normalizedResults = results.map((transaction) => ({
          id: transaction._id || transaction.id,
          invoiceNumber: transaction.transactionNumber,
          transactionNumber: transaction.transactionNumber,
          config: transaction.config?.toString ? transaction.config.toString() : transaction.config,
          configId: transaction.config?.toString ? transaction.config.toString() : transaction.config,
          idempotencyKey: transaction.idempotencyKey,
          lastSync: transaction.lastSync,
          createdDate: (() => {
            const date = new Date(transaction.createdAt);
            return !Number.isNaN(date.getTime()) ? date.toISOString() : null;
          })(),
          dueDate: (() => {
            const date = new Date(transaction.createdAt);
            return !Number.isNaN(date.getTime()) ? date.toISOString() : null;
          })(),
          customer: transaction.customer,
          status: { value: transaction.status, label: transaction.status },
          total: transaction.total,
          type: transaction.type,
          syncStatus: transaction.syncStatus,
          createdAt: transaction.createdAt,
          dataERP: transaction.dataERP,
          payments: transaction.payments || [],
          amountPaid: transaction.amountPaid,
          subtotal: transaction.subtotal,
          tax: transaction.tax,
          discount: transaction.discount,
          sourceType: transaction.sourceType,
          cancellation: transaction.cancellation,
          reversalOf: transaction.reversalOf,
          accountingRole: transaction.accountingRole,
          cancellationOperation: transaction.cancellationOperation,
          syncError: transaction.syncError,
          idERP: transaction.idERP,
          lastUpdatedDate: new Date().toISOString(),
          metadata: transaction.metadata,
          extraData: transaction.metadata,
          items: transaction.items.map((item) => ({
            ...item,
            // Normaliser d'autres dates d'éléments si nécessaire
          })),
        }));

        // A first-page refresh is authoritative for the visible list. Replacing
        // it removes returns/corrections that became hidden after cancellation;
        // subsequent pages are merged below.
        if (page === 1) state.allData = [];

        normalizedResults.forEach((invoice) => {
          const existing = state.allData.find((i) => i.id === invoice.id);
          if (existing) {
            Object.assign(existing, invoice);
          } else {
            state.allData.push(invoice);
          }
        });
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action['error'].message || action['error'];
      })
      .addCase(fetchOneInvoice.fulfilled, (state, action) => {
        const data = action.payload;
        if (data) {
          // Normaliser les dates avant de mettre à jour
          const normalizedData = {
            ...data,
            createdDate: data.createdDate ? new Date(data.createdDate).toISOString() : null,
            dueDate: data.dueDate ? new Date(data.dueDate).toISOString() : null,
            lastUpdatedDate: new Date().toISOString(),
            items:
              data.items?.map((item) => ({
                ...item,
                // Normaliser d'autres dates d'éléments si nécessaire
              })) || [],
          };

          const existing = state.allData.find((i) => i.id === data.id);
          if (existing) {
            Object.assign(existing, normalizedData);
          } else {
            state.allData.push(normalizedData);
          }
        }
      });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  resetInvoices,
  updateInvoices,
  removeInvoice,
  addDraft,
  removeDraft,
  updateDraft,
  addSync,
  removeSync,
  updateSync
} = slice.actions
